import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { catchError, Observable, of, ReplaySubject, shareReplay, switchMap, tap } from 'rxjs';

import { AuthUtils } from './auth.utils';
import { Title } from '@angular/platform-browser';
import { CurrentUserService } from '../api-users/api/currentUser.service';
import { AuthService } from '../api-users/api/auth.service';
import { CurrentUserResource } from '../api-users/model/currentUserResource';
import { UserResource } from '../api-users/model/userResource';

@Injectable()
export class AuthStingService {
    private _authenticated = false;

    private _user: ReplaySubject<UserResource> = new ReplaySubject<UserResource>(1);
    private observableUser: Observable<any> | null | undefined;
    private userSaved: any = null;
    private credentials?: { email: string; password: string }; // For 2F

    constructor(
        public translateService: TranslateService,
        private _httpClient: HttpClient,
        private _accessService: AuthService,
        private _currUserService: CurrentUserService,
        private titleService: Title,
    ) {}

    set accessToken(token: string) {
        localStorage.setItem('accessToken', token);
    }

    get accessToken(): string {
        // @ts-ignore
        return localStorage.getItem('accessToken') && localStorage.getItem('accessToken') !== 'undefined' ? localStorage.getItem('accessToken') : '';
    }

    set refreshToken(token: string) {
        localStorage.setItem('refreshToken', token);
    }

    get refreshToken(): string {
        return localStorage.getItem('refreshToken') ?? '';
    }

    forgotPassword(email: string): Observable<any> {
        const resetRequest: any = {
            email: email,
        };

        return this._accessService.sendResetLink(resetRequest).pipe(switchMap(response => of(response)));
    }

    public changePassword(data: { email: any; token: any; password: any; passwordConfirmation: any }): Observable<any> {
        const resetRequest: any = {
            email: data.email,
            token: data.token,
            password: data.password,
            passwordConfirmation: data.passwordConfirmation
        };

        return this._accessService.resetPassword(resetRequest).pipe(
            switchMap(response =>
                // Return a new observable with the response
                of(response)
            )
        );
    }

    signIn2FA(hash: string, pin: string): Observable<any> {
        return this._accessService.login2faAuthentication({ hash: hash, pin: pin }).pipe(
            switchMap((response: any) => {
                // Set access and refresh tokens
                this.accessToken = response.accessToken;
                this.refreshToken = response.refreshToken;
                this._authenticated = true;
                this.credentials = undefined;
                this.profile().subscribe();
                return of(true);
            }),
        );
    }

    tryToSignIn(credentials: { email: string; password: string }): Observable<any> {
        return this._accessService.loginBackofficeByEmailAndPassword(credentials).pipe(
            switchMap((response: any) => {
                this.credentials = credentials;
                if (response?.accessToken) {
                    this.accessToken = response.accessToken;
                    this.refreshToken = response.refreshToken;
                    this._authenticated = true;
                    this.credentials = undefined;
                    this.profile().subscribe();
                }

                return of(response);
            }),
        );
    }

    signInWithToken(token: string): Observable<any> {
        return this._accessService.verifyOneTimeToken(token).pipe(
            switchMap((response: any) => {
                // Set access and refresh tokens
                this.accessToken = response.accessToken;
                this.refreshToken = response.refreshToken;
                this._authenticated = true;
                this.credentials = undefined;
                this.profile().subscribe();
                return of(response);
            }),
        );
    }

    getCredentials(): { email: string; password: string } | undefined {
        return this.credentials;
    }

    tryToRefreshToken(): Observable<any> {
        return this._httpClient
            .post('/api/auth/refresh-token', {
                refreshToken: this.refreshToken,
            })
            .pipe(
                catchError(() => {
                    // Return false @todo: Delete local storage and setup _authenticated
                    this._authenticated = false;
                    localStorage.removeItem('accessToken');
                    localStorage.removeItem('refreshToken');
                    return of(false);
                }),
                switchMap((response: any) => {
                    if (response === false) {
                        this._authenticated = false;
                        localStorage.removeItem('accessToken');
                        localStorage.removeItem('refreshToken');
                        return of(false);
                    } else {
                        this.accessToken = response.accessToken;
                        this.refreshToken = response.refreshToken;
                        return of(true);
                    }
                }),
            );
    }

    signOut(): Observable<any> {
        return of(true).pipe(
            tap(() => {
                this.accessToken = '';
                this.refreshToken = '';
                this._authenticated = false;

                console.log(this.accessToken);
            }),
        );
    }

    check(): Observable<boolean> {
        // Check if the user is logged in
        if (this._authenticated) {
            return of(true);
        }

        // Check the access token availability
        if (!this.accessToken) {
            return of(false);
        }

        // Check the access token expire date
        if (AuthUtils.isTokenExpired(this.accessToken)) {
            return this.tryToRefreshToken();
        }

        // If the access token exists and it didn't expire, sign in using it
        return of(true);
    }

    get(): Observable<CurrentUserResource> {
        return this._currUserService.getCurrentUser();
    }

    reloadProfile() {
        this._currUserService.getCurrentUser().subscribe(user => (this.userSaved = user.user));
    }

    public profile(): Observable<any> {
        if (this.userSaved) {
            return of(this.userSaved);
        } else if (this.observableUser) {
            return this.observableUser;
        } else {
            this.observableUser = this.get().pipe(
                tap((user: any) => {
                    this.userSaved = user;
                    this.observableUser = null; // Reset the observable once the user is retrieved
                }),
                shareReplay(1), // Cache the result to share with subsequent subscribers
            );
            return this.observableUser;
        }
    }

    public clearLoggedUserCache() {
        this.userSaved = null;
        this.observableUser = null;
    }

    validatePassword(control: AbstractControl): { [key: string]: any } | null {
        const value: string = control.value;
        const hasNumber = /\d/.test(value);
        const hasUppercase = /[A-Z]/.test(value);
        const hasLowercase = /[a-z]/.test(value);
        const hasMinimumLength = value && value.length >= 8;
        const conditionMessages = [];

        let errorMessage = this.translateService.instant('section.pass.mustContain');
        let error = false;

        if (!hasNumber) {
            error = true;
            conditionMessages.push(this.translateService.instant('section.pass.number'));
        }

        if (!hasUppercase) {
            error = true;
            conditionMessages.push(this.translateService.instant('section.pass.uppercase'));
        }

        if (!hasLowercase) {
            error = true;
            conditionMessages.push(this.translateService.instant('section.pass.lowercase'));
        }

        if (!hasMinimumLength) {
            error = true;
            conditionMessages.push(this.translateService.instant('section.pass.minimumLength'));
        }

        if (error) {
            if (conditionMessages.length > 2) {
                conditionMessages.splice(conditionMessages.length - 1, 0, ' a ');
                for (let i = conditionMessages.length - 3; i >= 1; i--) {
                    conditionMessages.splice(i, 0, ', ');
                }
                errorMessage += conditionMessages.join('');
            } else if (conditionMessages.length > 1) {
                conditionMessages.splice(conditionMessages.length - 1, 0, ' a ');
                errorMessage += conditionMessages.join(' ');
            } else {
                errorMessage += conditionMessages.join(' ');
            }

            return { invalidPassword: errorMessage };
        }
        return null;
    }

    checkPasswords(group: FormGroup) {
        if (!group.controls.password || !group.controls.passwordConfirmation) {
            return true;
        }
        const pass = group.controls.password.value;
        const confirmPass = group.controls.passwordConfirmation.value;
        group.controls['passwordConfirmation'].setErrors(pass === confirmPass ? group.controls['passwordConfirmation'].errors : { notSame: true });
        return pass === confirmPass ? null : { notSame: true };
    }

    public createPassword(passReq: any): Observable<any> {
        console.log(passReq);
        return of(false);
        // return this._accessService.createPassword(passReq);
    }

    // public getUser(idUser: string) {
    //     return this._usersDsService.getUserById(idUser).pipe(
    //         tap(user => {
    //             this._user.next(user);
    //         }),
    //     );
    // }

    public resendVerification(email: string): Observable<any> {
        console.log(email);
        return of(false);
        // return this._usersDsService.resendEmail({ email: email });
    }

    public getLogoPath(): string {
        if (this.userSaved?.company && this.userSaved?.company[0] && this.userSaved?.company[0]?.avatar) {
            return this.userSaved.company[0].avatar;
        } else if (this.userSaved?.avatar) {
            return this.userSaved.avatar;
        } else {
            return 'assets/images/logo/sting_logo.png';
        }
    }

    public setTitle(newTitle: string) {
        this.titleService.setTitle(newTitle);
    }
}
